import { FC } from 'react';
import { withStyles } from '../../../utils';
import MaterialSymbol from '../../DataDisplay/MaterialSymbol/MaterialSymbol';
import styles from './CheckboxIndicator.module.scss';
import { motion, AnimatePresence } from 'motion/react';

const cn = withStyles(styles);
type State = boolean | 'indeterminate';

type CheckboxIndicatorProps = {
  checked: State;
  disabled?: boolean;
};

/**
 * Visual-only; has no interactivity on its own.
 * examples:
 * @example
 * Put an input[type="checkbox"] AFTER it if you want it to be a checkbox.
 * ```
 * <label>
 *  <CheckboxIndicator checked={checked} />
 *  <input type="checkbox" onChange={toggleChecked} />
 * </label>
 * ```
 * @example
 * Wrap it in a button if you want it to be a button.
 * ```
 * <button onChange={toggleChecked}>
 *  <CheckboxIndicator checked={checked} />
 * </button>
 * ```
 *
 * @example
 * It can just be a visual representation of some other thing being activated/etc
 * ```
 * <button onClick={toggleChecked}>Toggle It</button>
 * ...
 * <CheckboxIndicator checked={checked} />
 * ```
 */
const CheckboxIndicator: FC<CheckboxIndicatorProps> = ({ checked, disabled = false }) => {
  return (
    <motion.span
      className={cn(
        'checkboxIndicatorContainer',
        checked === true && 'checked',
        checked === 'indeterminate' && 'indeterminate',
        disabled && 'disabled',
      )}
    >
      <AnimatePresence mode="wait">
        {/* {checked === false && <motion.div key="unchecked">.</motion.div>} */}
        {checked === true && (
          <motion.div
            key="checked"
            style={{
              transformOrigin: 'center center',
            }}
            initial={{
              opacity: 0,
              scale: 0,
            }}
            animate={{
              opacity: 1,

              scale: 1,
              transition: {
                duration: 0.15,
                ease: 'backOut',
                // delay: 0.2,
              },
            }}
            exit={{
              opacity: 0,
              scale: 0,
              transition: {
                duration: 0.15,
                ease: 'backIn',
              },
            }}
          >
            <MaterialSymbol icon="check" className={cn('checkmark')} />
          </motion.div>
        )}

        {checked === 'indeterminate' && (
          <motion.div
            key="indeterminate"
            style={{
              transformOrigin: 'center center',
            }}
            initial={{
              opacity: 0,
              scaleX: 0,
            }}
            animate={{
              opacity: 1,
              scaleX: 1,
              transition: {
                duration: 0.15,
                ease: 'backOut',
                // delay: 0.2,
              },
            }}
            exit={{
              opacity: 0,
              scaleX: 0,
              transition: {
                duration: 0.15,
                ease: 'backIn',
              },
            }}
          >
            <MaterialSymbol icon="check_indeterminate_small" className={cn('checkmark')} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.span>
  );
};

export default CheckboxIndicator;
